import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, CircularProgress, Alert, Button, Chip } from '@mui/material';
import { Store as StoreIcon } from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { fetchMerchantProfile } from '../../store/slices/merchantSlice';

interface MerchantProtectedRouteProps {
  children: React.ReactNode;
}

const MerchantProtectedRoute: React.FC<MerchantProtectedRouteProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { profile, loadingProfile } = useSelector((state: RootState) => state.merchant);

  useEffect(() => {
    if (!profile) {
      dispatch(fetchMerchantProfile());
    }
  }, [dispatch, profile]);

  if (loadingProfile) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: { xs: 300, md: 400 },
        gap: { xs: 1.5, md: 2 },
        px: { xs: 2, sm: 3 },
      }}>
        <CircularProgress size={48} sx={{ color: '#11C1EC' }} />
        <Typography variant="body1" sx={{ color: '#7F839C' }}>
          Checking merchant status...
        </Typography>
      </Box>
    );
  }

  // No merchant profile — redirect to application
  if (!profile) {
    return <Navigate to="/merchant/apply" replace />;
  }

  // Merchant exists but not approved
  if (profile.status !== 'approved') {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: { xs: 300, md: 400 },
        textAlign: 'center',
        gap: { xs: 1.5, md: 2 },
        px: { xs: 2, sm: 3 },
      }}>
        <Box sx={{
          width: { xs: 60, md: 72 },
          height: { xs: 60, md: 72 },
          borderRadius: '50%',
          background: profile.status === 'pending'
            ? 'rgba(255, 165, 0, 0.1)'
            : profile.status === 'rejected'
              ? 'rgba(255, 71, 87, 0.1)'
              : 'rgba(128, 128, 128, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <StoreIcon sx={{
            fontSize: { xs: 28, md: 36 },
            color: profile.status === 'pending'
              ? '#E88A00'
              : profile.status === 'rejected'
                ? '#FF4757'
                : '#7F839C',
          }} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 700, color: '#202647', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          {profile.status === 'pending' && 'Merchant Verification Pending'}
          {profile.status === 'rejected' && 'Merchant Application Rejected'}
          {profile.status === 'suspended' && 'Merchant Account Suspended'}
        </Typography>

        <Typography variant="body1" sx={{ color: '#7F839C', maxWidth: { xs: '100%', md: 450 }, fontSize: { xs: '0.85rem', md: '1rem' } }}>
          {profile.status === 'pending' &&
            'Your merchant application is being reviewed. You will be notified once approved.'}
          {profile.status === 'rejected' &&
            (profile.rejectionReason || 'Your application was not approved. Please resubmit with updated information.')}
          {profile.status === 'suspended' &&
            'Your merchant account has been suspended. Please contact support for assistance.'}
        </Typography>

        <Chip
          label={
            profile.status === 'pending' ? 'Awaiting Verification' :
            profile.status === 'rejected' ? 'Rejected' : 'Suspended'
          }
          sx={{
            fontWeight: 600,
            fontSize: { xs: '0.8rem', md: '0.85rem' },
            py: 2,
            px: 1,
            bgcolor: profile.status === 'pending'
              ? 'rgba(255, 165, 0, 0.1)'
              : profile.status === 'rejected'
                ? 'rgba(255, 71, 87, 0.1)'
                : 'rgba(128, 128, 128, 0.1)',
            color: profile.status === 'pending'
              ? '#E88A00'
              : profile.status === 'rejected'
                ? '#FF4757'
                : '#7F839C',
          }}
        />

        {profile.status === 'rejected' && (
          <Button
            variant="contained"
            href="/merchant/apply"
            sx={{
              mt: 1,
              background: 'linear-gradient(135deg, #11C1EC, #09486D)',
              borderRadius: 2,
              '&:hover': { background: 'linear-gradient(135deg, #0DB5DE, #07375A)' },
            }}
          >
            Resubmit Application
          </Button>
        )}
      </Box>
    );
  }

  // Merchant is approved — render children
  return <>{children}</>;
};

export default MerchantProtectedRoute;
